import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import {
  Typography,
  Box,
  Stack,
  FormHelperText,
  FormControl,
  InputLabel,
  Divider,
  Skeleton,
} from "@mui/material";
import { LoadingButton } from "../LoadingButton.js";
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiRoute, getPageRoute } from "src/services/Config.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { customEvent } from "src/utils/gtag.js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import abTestingService from "src/services/ABTestingService.js";
import ActionCard from "../ActionCard.js";
import {
  useSimplePayment,
} from "src/utils/useSimplePayment.js";

const DEFAULT_SUCCESS_URL = getPageRoute(
  "upgrade",
  "PAYMENT_STANZA_PLUS_CONFIRMATION"
);

const PaymentForm = ({
  type = "consumer_annual",
  trialPeriodDays = 7,
  successUrl = DEFAULT_SUCCESS_URL,
}) => {
  // Auth and navigation hooks
  const { user, refetchAuth } = useContext(AuthContext);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const { calendar = null } = location.state || {};
  const { subscribe } = useSubscribe();
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  // Component state
  const [paymentRequest, setPaymentRequest] = useState(null); // Apple Pay/Google Pay
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isSimplePayment, setSimplePaymentFailed } = useSimplePayment({});

  // Fetch price details from Stripe API
  const fetchPriceDetails = async () => {
    if (stripe && type) {
      try {
        const response = await axios.get(
          getApiRoute("upgrade", "STRIPE_PRICE_DETAILS", {}, { type })
        );

        // Track successful payment form ready
        customEvent({
          name: "native_checkout_loaded_successfully",
          category: "NativeCheckout",
          label: "Native Checkout Form Ready for Payment",
          data: {
            user_id: user?._id,
            user_email: user?.email,
          },
        });

        return response.data;
      } catch (error) {
        setError("Failed to retrieve price details.");
        return null;
      }
    } else {
      return null;
    }
  };

  const {
    data: priceDetails,
    isFetching: isLoadingPriceDetails,
    isError: isPriceDetailsError,
    refetch: refetchStripePriceDetails,
  } = useQuery([`stripe-price-details-${type}`], fetchPriceDetails, {
    enabled: !!stripe && !!type,
  });

  // Track component load
  useEffect(() => {
    customEvent({
      name: "native_checkout_component_loaded",
      category: "NativeCheckout",
      label: "Native Checkout Component Loaded",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        payment_type: type,
        trial_period_days: trialPeriodDays,
      },
    });
  }, []);

  // Clear errors when plan type changes
  useEffect(() => {
    setError(null);
  }, [type]);

  // Redirect users who already have a subscription
  useEffect(() => {
    if (user?.hasValidSubscription) {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "You already have an active Stanza+ subscription.",
        "success"
      );
      setTimeout(() => {
        navigate(getPageRoute("home", "HOME"));
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch price details when Stripe loads
  useEffect(() => {
    refetchStripePriceDetails();
  }, [stripe]);

  // Track payment failures for analytics and error reporting
  const trackPaymentFailure = useCallback(
    (message, error = new Error(), description = "", logToRollbar = true) => {
      customEvent({
        name: `"native_payment_failure`,
        category: "Upgrade",
        label: "Failure Native Payment",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
          message,
        },
      });
      customEvent({
        name: `${isSimplePayment ? "simple" : "regular"}_payment_failure`,
        category: "Upgrade",
        label: "Failure Native Payment",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
          message,
        },
      });

      rollbar.error(description || "Native Payment Failure", error, {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
        trial_period: trialPeriodDays > 0,
        message,
        hasStripeInstance: Boolean(stripe),
        hasElements: Boolean(elements),
        isSimplePayment,
      });
    },
    [
      elements,
      isSimplePayment,
      stripe,
      trialPeriodDays,
      user._id,
      user.email,
      user.handle,
    ]
  );

  // Main payment processing function - handles both card and payment request flows
  const handlePayment = async ({
    isPaymentRequest,
    paymentMethodId,
    event,
  }) => {
    if (!isPaymentRequest) event.preventDefault();
    setLoading(true);

    try {
      // Track analytics for trial payments
      if (trialPeriodDays > 0) {
        customEvent({
          name: "trial_period_payment",
          category: "Upgrade",
          label: "Requested Trial Period Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
          },
        });

        // Track A/B test conversions
        try {
          abTestingService.trackConversionForAllExperiments(
            "trial_period_payment",
            user,
            {
              user_id: user._id,
              user_email: user.email,
            }
          );
        } catch (error) {
          // Don't fail payment if A/B tracking fails
          console.warn("A/B test conversion tracking failed:", error);
        }
      }

      // Use the provided payment method ID or create one using the CardElement
      let methodId = paymentMethodId;

      if (!isPaymentRequest && !isSimplePayment) {
        if (!stripe) {
          // Error in stripe instance
          throw new Error("Stripe has not loaded yet.");
        }
        if (!elements) {
          // Error in stripe elements
          throw new Error("Stripe elements has not loaded yet.");
        }

        if (cardError || !cardComplete) {
          setError("Please enter your card details.");
          setLoading(false);
          trackPaymentFailure("Invalid card details", null, "", false);
          return;
        }

        const { error: stripeError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement),
            billing_details: {
              email: user.email,
            },
          });

        if (stripeError) {
          // Error in createPaymentMethod
          throw stripeError;
        }

        methodId = paymentMethod.id;
      }

      // Proceed with the backend call
      const response = await axios.post(
        getApiRoute("upgrade", "STRIPE_NATIVE_CHECKOUT"),
        {
          type,
          ...(!isSimplePayment && { paymentMethodId: methodId }),
          trialPeriodDays,
        },
        { withCredentials: true }
      );

      const { clientSecret } = response.data;

      // Process the payment
      // When there is a new trial, there is no clientSecret generated as there is no payment
      if (clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret
        );
        if (confirmError) {
          // Error in confirmCardPayment
          throw confirmError;
        }
      } else if (!trialPeriodDays) {
        // Error in clientSecret
        throw new Error("Failed on starting checkout session.");
      }

      // Success: Update auth and navigate
      await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 sec to leave the backend update upgrade status
      await refetchAuth();
      await setSimplePaymentFailed(false); // User is read for doing future simple payments
      customEvent({
        name: `native_payment_successful`,
        category: "Upgrade",
        label: "Successful Native Payment",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
        },
      });
      customEvent({
        name: `${isSimplePayment ? "simple" : "regular"}_payment_successful`,
        category: "Upgrade",
        label: "Successful Native Payment",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
        },
      });
      event?.complete?.("success"); // Handle PaymentRequest event completion
      if (calendar) await subscribe({ calendar });
      navigate(successUrl);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "An error occurred";
      setError(errorMessage);
      if (user?.stripeCustomerId) {
        setSimplePaymentFailed(true); // Updates the UI showing CC fields
        setError(errorMessage);
      }
      trackPaymentFailure(errorMessage, err);
      event?.complete?.("fail"); // Handle PaymentRequest event completion
    } finally {
      setLoading(false); // always gets executed
    }
  };

  // Initialize Apple Pay/Google Pay payment request
  const {
    mutateAsync: initializePaymentRequest,
    isLoading: isLoadingPaymentRequest,
    isError: isErrorPaymentRequest,
    error: errorPaymentRequest,
  } = useMutation(async () => {
    if (stripe && priceDetails) {
      const { amount, label, currency } = priceDetails;

      const pr = stripe.paymentRequest({
        country: "US",
        currency: currency || "usd",
        total: {
          label: trialPeriodDays > 0 ? `${trialPeriodDays} free days` : label,
          amount: trialPeriodDays > 0 ? 0 : amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ["link"],
      });

      const result = await pr.canMakePayment();
      if (result) {
        setPaymentRequest(pr);
      } else {
        setPaymentRequest(null);
      }
    }
  });

  useEffect(() => {
    if (stripe && priceDetails) {
      initializePaymentRequest();
    }
  }, [stripe, priceDetails]);

  useEffect(() => {
    if (isErrorPaymentRequest) {
      trackPaymentFailure(
        errorPaymentRequest?.message,
        errorPaymentRequest,
        "Failure on initializePaymentRequest"
      );
      setError("Failed to initialize payment request.");
    }
  }, [errorPaymentRequest, isErrorPaymentRequest]);

  // Handle Apple Pay/Google Pay payment method selection
  const handlePaymentMethodReceived = async (event) => {
    await handlePayment({
      isPaymentRequest: true,
      paymentMethodId: event.paymentMethod.id,
      event,
    });
  };

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on("paymentmethod", handlePaymentMethodReceived);
    }
  }, [paymentRequest]);

  // Handle traditional card form submission
  const handleSubmit = async (event) => {
    await handlePayment({
      event,
      isPaymentRequest: false,
      paymentMethodId: null,
    });
  };

  // Stripe card element styling
  const cardElementStyles = {
    base: {
      color: "#000000",
      fontSize: "16px",
      fontFamily: ["Satoshi", "Nunito", "Open Sans", "Arial"].join(","),
      "::placeholder": {
        color: "rgba(0, 0, 0, 0.54)",
      },
    },
    invalid: {
      color: "#FF4C4C",
    },
  };

  const cardElementWrapperStyles = {
    padding: "18.5px 14px",
    borderRadius: "4px",
    border: `1px solid ${
      cardFocused ? "#000000" : cardError ? "#f44336" : "rgba(0, 0, 0, 0.23)"
    }`,
    "&:hover": {
      borderColor: cardFocused ? "#000000" : "rgba(0, 0, 0, 0.87)",
    },
    position: "relative",
  };

  // Show loading skeleton while price details are being fetched
  if (isLoadingPriceDetails) {
    return (
      <Skeleton
        animation="wave"
        component="div"
        variant="rounded"
        sx={{ borderRadius: "4px", height: "200px", mb: 2.5 }}
      />
    );
  }

  // Show error state if price details failed to load
  if (isPriceDetailsError || (!priceDetails && !isLoadingPriceDetails)) {
    return (
      <ActionCard
        title="No price details"
        description="Failed to retrieve price details."
        primaryButtonCTA="Try Refreshing"
        primaryAction={() => navigate(getPageRoute("upgrade", "UPGRADE_PLUS"))}
        iconName="highlight_off"
      />
    );
  }

  return (
    <>
      {isSimplePayment ? (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            {error && <Typography color="error">{error}</Typography>}
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              disabled={!stripe}
              loading={loading}
              fullWidth
              sx={{ mt: 2, width: "100%" }}
            >
              {trialPeriodDays > 0 ? "Pay $0 Today" : "Pay Now"}
            </LoadingButton>
          </Stack>
        </form>
      ) : (
        <>
          {/* Apple Pay/Google Pay button section */}
          {isLoadingPaymentRequest ? (
            <Skeleton
              animation="wave"
              component="div"
              variant="rounded"
              sx={{ borderRadius: "4px", height: "40px", mb: 2.5 }}
            />
          ) : paymentRequest ? (
            <>
              <PaymentRequestButtonElement
                options={{ paymentRequest }}
                style={{
                  paymentRequestButton: {
                    type: "default", // 'default' | 'donate' | 'buy' | 'book' | 'checkout' | 'subscribe'
                    theme: "dark", // 'dark' | 'light' | 'light-outline'
                  },
                }}
              />
              <Divider sx={{ my: 2.5 }}>
                <Typography variant="subtitle2">or pay with card</Typography>
              </Divider>
            </>
          ) : (
            <></>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <FormControl fullWidth variant="outlined">
                <InputLabel
                  shrink
                  error={!!cardError}
                  sx={{
                    backgroundColor: "#fff",
                    padding: "0 4px",
                    pointerEvents: "none",
                  }}
                >
                  Card Details
                </InputLabel>
                <Box
                  sx={cardElementWrapperStyles}
                  onClick={() => {
                    const cardElement = elements?.getElement(CardElement);
                    cardElement?.focus();
                  }}
                >
                  <CardElement
                    options={{
                      style: cardElementStyles,
                      hidePostalCode: true,
                      disableLink: true,
                    }}
                    onChange={(event) => {
                      setCardError(event.error ? event.error.message : null);
                      setCardComplete(event.complete);
                    }}
                    onFocus={() => setCardFocused(true)}
                    onBlur={() => setCardFocused(false)}
                  />
                </Box>
                {cardError && (
                  <FormHelperText error>{cardError}</FormHelperText>
                )}
              </FormControl>
              {error && <Typography color="error">{error}</Typography>}
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                disabled={!stripe}
                loading={loading}
                fullWidth
                sx={{ mt: 2, width: "100%" }}
              >
                {trialPeriodDays > 0 ? "Pay $0 Today" : "Pay Now"}
              </LoadingButton>
            </Stack>
          </form>
        </>
      )}
    </>
  );
};

export default PaymentForm;
